import React from "react";

import { ILocation } from "../../../models/location";
import { preferredLocationSelector } from "../../../store/common/selectors";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { isZipLocation } from "../../../utils/guards";
import { PredictedDeliveryDateStatus } from "../constants";
import { IDeliveryDatePredictionState } from "../reducers.interfaces";
import { loadPredictedDeliveryDate } from "../thunks";

const PREDICTION_TTL = 3600 * 1000; // 1 hour

const shouldLoadPrediction = (
    location: ILocation | null,
    prediction: IDeliveryDatePredictionState | undefined,
): location is ILocation & { zip: string } => {
    // If we don't have a ZIP code, we can't load a prediction.
    if (!location || !isZipLocation(location)) {
        // TODO(i18n): This is a developer-facing log message - no translation needed
        console.log("Location is not suitable for delivery date prediction");
        return false;
    }
    // If prediction state is missing (e.g. redux-persist rehydrated old
    // data without this field), we should load it.
    if (!prediction) {
        return true;
    }
    // If the prediction is already loading, just wait for it.
    if (prediction.status === PredictedDeliveryDateStatus.LOADING) {
        // TODO(i18n): This is a developer-facing log message - no translation needed
        console.log("Delivery date prediction is already loading");
        return false;
    }
    // If we've already loaded data for this ZIP code and got back an
    // error, don't bother trying to load the data again.
    if (
        prediction.status === PredictedDeliveryDateStatus.ERROR &&
        location.zip === prediction.data.postcode
    ) {
        // TODO(i18n): This is a developer-facing log message - no translation needed
        console.log("Delivery date prediction is already up to date");
        return false;
    }
    // If we have a valid prediction, and it's not too old (1 hour TTL), don't
    // bother loading the data again.
    if (
        prediction.status === PredictedDeliveryDateStatus.LOADED &&
        location.zip === prediction.data.postcode &&
        prediction.data.created_datetime
    ) {
        const createdTimestamp = new Date(
            prediction.data.created_datetime,
        ).getTime();
        const expiresOnTimestamp = createdTimestamp + PREDICTION_TTL;
        const nowTimestamp = new Date().getTime();
        if (expiresOnTimestamp > nowTimestamp) {
            // TODO(i18n): This is a developer-facing log message - no translation needed
            console.log("Delivery date prediction is already up to date");
            return false;
        }
    }
    return true;
};

export const PredictedDeliveryLoader = (): React.ReactNode => {
    const dispatch = useAppDispatch();
    const location = useAppSelector(preferredLocationSelector);
    const prediction = useAppSelector(
        (state) => state.checkout.data.predicted_delivery_date,
    );

    React.useEffect(() => {
        if (shouldLoadPrediction(location, prediction)) {
            // We've passed all the gates, so fetch the prediction now.
            void dispatch(
                loadPredictedDeliveryDate({ postcode: location.zip }),
            );
        }
    }, [location, prediction, dispatch]);

    return null;
};
